"use client"

import type React from "react"
import { useEffect, useRef } from "react"

interface CursorRevealProps {
  children: React.ReactNode
  /** Diameter of the reveal circle in px */
  size?: number
  /** Color of the overlay that hides content (use rgba) */
  overlay?: string
  /** Backdrop blur applied to hidden areas in px (0 = no blur) */
  blur?: number
  className?: string
  style?: React.CSSProperties
}

/**
 * Wraps content with a dark overlay. Moving the cursor over it
 * cuts a circular hole in the overlay, revealing what's beneath.
 */
export const CursorReveal: React.FC<CursorRevealProps> = ({
  children,
  size = 180,
  overlay = "rgba(9,9,11,0.92)",
  blur = 0,
  className,
  style,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = containerRef.current
    const overlayEl = overlayRef.current
    if (!container || !overlayEl) return

    const r = size / 2

    const onMove = (e: MouseEvent) => {
      const rect = container.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      const mask = `radial-gradient(circle ${r}px at ${x}px ${y}px, transparent 0%, black ${r}px)`
      overlayEl.style.maskImage = mask
      ;(overlayEl.style as any).webkitMaskImage = mask
    }

    const onLeave = () => {
      overlayEl.style.maskImage = ""
      ;(overlayEl.style as any).webkitMaskImage = ""
    }

    container.addEventListener("mousemove", onMove, { passive: true })
    container.addEventListener("mouseleave", onLeave)

    return () => {
      container.removeEventListener("mousemove", onMove)
      container.removeEventListener("mouseleave", onLeave)
    }
  }, [size])

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ position: "relative", overflow: "hidden", ...style }}
    >
      {children}
      <div
        ref={overlayRef}
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background: overlay,
          backdropFilter: blur ? `blur(${blur}px)` : undefined,
          WebkitBackdropFilter: blur ? `blur(${blur}px)` : undefined,
          pointerEvents: "none",
        }}
      />
    </div>
  )
}
